import React, { useEffect, useMemo, useState } from 'react';

import { ArrowRight, Check } from '@mui/icons-material';
import { Box, Button, Container, Grid, Paper, Typography } from '@mui/material';
import { Subheader, TextInput } from 'components';
import { useAlerts, useBenchmarkSubmissions } from 'hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { capitalize } from 'lodash';

export default function BenchmarkDrillSubmissionFeedback() {
	const {
		findBenchmarkSubmission,
		benchmarkSubmission,
		submitBenchmarkFeedback
	} = useBenchmarkSubmissions();
	const { id } = useParams();
	const { showAlertWarning, showAlertSuccess, showAlertError } = useAlerts();
	const navigate = useNavigate();

	const [feedback, setFeedback] = useState({
		comment: '',
		skill_ratings: {}
	});

	const playercardKeys = useMemo(() => {
		if (benchmarkSubmission?.user) {
			benchmarkSubmission?.user.player_card;
			const keys = Object.keys(benchmarkSubmission?.user.player_card).filter(
				(key) => !['id', 'overall', 'position', 'unlocked'].includes(key)
			);

			return keys;
		}
		return [];
	}, [benchmarkSubmission]);

	const handleSaveFeedback = async () => {
		try {
			const response = await submitBenchmarkFeedback(id, feedback);

			if (response && response.data) {
				showAlertSuccess('Feedback sent');
				return navigate(-1);
			}

			showAlertError('Feedback failed, try again later');
		} catch (error) {
			showAlertError('Feedback failed, try again later');
		}
	};

	const handleSkillScoreChange = (e, skill) => {
		const { name, value } = e.target;

		if (value > 100) {
			showAlertWarning('Maximum Skill Rating Allowed Is 100');

			return;
		}

		setFeedback({
			...feedback,
			skill_ratings: {
				...feedback.skill_ratings,
				[skill.toLowerCase()]: Number(value)
			}
		});
	};

	const handleCommentChange = (e) => {
		const { name, value } = e.target;
		setFeedback({ ...feedback, [name]: value });
	};

	useEffect(() => {
		findBenchmarkSubmission(id);
	}, [id]);

	useEffect(() => {
		if (benchmarkSubmission.user && playercardKeys) {
			if (benchmarkSubmission.feedback) {
				setFeedback(benchmarkSubmission.feedback);
			} else {
				const playerRatings = {};
				playercardKeys.forEach(
					(key) =>
						(playerRatings[key] = benchmarkSubmission.user.player_card[key])
				);

				setFeedback({
					...feedback,
					skill_ratings: playerRatings
				});
			}
		}
	}, [benchmarkSubmission]);

	return (
		<React.Fragment>
			<Subheader
				title={'Benchmark Submission'}
				buttons={[
					<Button
						color="primary"
						variant="contained"
						startIcon={<ArrowRight />}
						onClick={handleSaveFeedback}
					>
						Save
					</Button>
				]}
			/>

			<Container>
				<Paper>
					<Box p={4}>
						{benchmarkSubmission?.submissions &&
							benchmarkSubmission?.submissions.map((submission) => (
								<Grid container key={submission.id}>
									<Grid
										item
										xs={12}
										md={6}
										sx={{
											p: 2,
											borderBottom: '1px solid',
											borderColor: 'grey.300',
											height: 400
										}}
									>
										<Box sx={{ color: 'text.secondary', textAlign: 'center' }}>
											<Typography variant="caption">Benchmark Video</Typography>
										</Box>
										<video
											width="100%"
											height={'90%'}
											controls
											src={submission?.lesson?.video_url}
										/>
									</Grid>
									<Grid
										item
										xs={12}
										md={6}
										sx={{
											p: 2,
											borderBottom: '1px solid',
											borderColor: 'grey.300',
											height: 400
										}}
									>
										<Box sx={{ color: 'text.secondary', textAlign: 'center' }}>
											<Typography variant="caption">
												Users Uploaded Attempt
											</Typography>
										</Box>
										<video
											width="100%"
											height={'90%'}
											controls
											src={submission?.video_url}
										/>
									</Grid>
								</Grid>
							))}

						<Grid item xs={12} sm={12}>
							<Box sx={{ marginTop: 20, marginBottom: 20 }}>
								<Box sx={{ marginTop: 5, marginBottom: 5 }}>
									<Typography variant="h5">User Playercard</Typography>
								</Box>
								<Grid container spacing={2}>
									{playercardKeys.map((key, index) => (
										<Grid
											key={`${key}+${index}`}
											item
											xs={12}
											sm={4}
											sx={{
												display: 'flex',
												flexDirection: 'column'
											}}
										>
											<Typography variant="subtitle1">
												{capitalize(key)}
											</Typography>
											<Typography variant="caption">
												{(benchmarkSubmission?.user.player_card &&
													benchmarkSubmission?.user.player_card[key]) ||
													'-'}
											</Typography>
										</Grid>
									))}
								</Grid>
							</Box>
						</Grid>

						<Grid item xs={12} sm={12}>
							<Box sx={{ marginTop: 20, marginBottom: 10 }}>
								<Typography variant="h5">Feedback</Typography>
								<TextInput
									value={feedback?.comment || ''}
									label=""
									name="comment"
									placeholder="Feedback"
									type={'text'}
									multiline={true}
									handleChange={handleCommentChange}
									rows={10}
								/>
							</Box>
							<Grid container spacing={2} sx={{ paddingBottom: 10 }}>
								{playercardKeys?.map((skill, index) => (
									<Grid
										key={`${skill}+${index}`}
										item
										xs={12}
										sm={4}
										sx={{
											display: 'flex',
											flexDirection: 'column'
										}}
									>
										<TextInput
											key={index}
											value={feedback.skill_ratings[skill.toLowerCase()] || ''}
											label={capitalize(skill)}
											name={skill.toLowerCase()}
											handleChange={(e) => handleSkillScoreChange(e, skill)}
											placeholder={skill}
											type={'number'}
										/>
									</Grid>
								))}
							</Grid>
						</Grid>

						<Box py={2}>
							<Button
								color="primary"
								variant="contained"
								startIcon={<Check />}
								onClick={handleSaveFeedback}
							>
								Save
							</Button>
						</Box>
					</Box>
				</Paper>
			</Container>
		</React.Fragment>
	);
}
